/**
 * SERVICE TIER CONFIGURATION
 * Three-tier model (Phase 6 - Full Service Removed Dec 31, 2025):
 * Fixed-Price Model (Phase 7 - Jan 2026):
 * - ALL tiers charge a flat 15% material markup (one price, no member discounts)
 * - Materials Only: DIY customers, 15% markup
 * - BYOI Basic: Customer's installer, 15% markup, No coordination fee
 * - BYOI Certified: Certified installer, 15% markup, No fee + QMA/NCR
 *
 * Lead-Attribution Commission Split:
 * - 15% margin − 3% Stripe = 12% distributable
 * - Installer-generated lead → installer keeps full 12%
 * - Platform-generated lead → installer gets tier rebate (5–12%), CID keeps rest
 */

/**
 * VALID_QUOTE_TYPES
 * Allowed values for quote_type field
 * NOTE: 'full_service' is deprecated but may exist in legacy quotes
 */
const VALID_QUOTE_TYPES = ['byoi_certified', 'byoi', 'materials_only'];

/**
 * LEGACY_QUOTE_TYPE_MAPPING
 * Maps deprecated quote types to their modern equivalents
 */
const LEGACY_QUOTE_TYPE_MAPPING = {
  'full_service': 'byoi_certified'  // Full Service quotes treated as BYOI Certified
};
/**
 * Quote Flow Constants
 * Centralized constants for room types, property types, and pricing multipliers
 */

/**
 * ROOM TYPES
 * Standard room types supported in Quote Flow Step 2
 */
const ROOM_TYPES = {
  // Main Living Areas
  LIVING_ROOM: 'living_room',
  DINING_ROOM: 'dining_room',
  FAMILY_ROOM: 'family_room',
  
  // Bedrooms
  MASTER_BEDROOM: 'master_bedroom',
  BEDROOM: 'bedroom',  // Generic bedroom (can add Bedroom 2, 3, 4 dynamically)
  GUEST_ROOM: 'guest_room',
  
  // High-Complexity Areas
  HALLWAY: 'hallway',
  STAIRS: 'stairs',  // Special pricing - typically 2-3x normal install rate
  LANDING: 'landing',
  
  // Other Common Areas
  BASEMENT: 'basement',
  OFFICE: 'office',
  BONUS_ROOM: 'bonus_room',
  CLOSET: 'closet',
  
  // Allow custom rooms
  CUSTOM: 'custom'
};

/**
 * ROOM DISPLAY NAMES
 * User-friendly labels for each room type
 */
const ROOM_LABELS = {
  living_room: 'Living Room',
  dining_room: 'Dining Room',
  family_room: 'Family Room',
  master_bedroom: 'Master Bedroom',
  bedroom: 'Bedroom',
  guest_room: 'Guest Room',
  hallway: 'Hallway',
  stairs: 'Stairs',
  landing: 'Landing',
  basement: 'Basement',
  office: 'Office / Den',
  bonus_room: 'Bonus Room',
  closet: 'Closet',
  custom: 'Custom Room',
  // --- Commercial ---
  reception: 'Reception',
  open_office: 'Open Office',
  private_office: 'Private Office',
  conference_room: 'Conference Room',
  break_room: 'Break Room',
  lobby: 'Lobby / Foyer',
  restroom: 'Restroom',
  retail_floor: 'Sales Floor',
  // --- Multi-Unit ---
  unit_living: 'Living Room',
  unit_bedroom: 'Bedroom',
  unit_hallway: 'Hallway',
  unit_stairs: 'Stairs',
  unit_closet: 'Closet',
  unit_kitchenette: 'Kitchenette',
  unit_bathroom: 'Bathroom'
};

/**
 * ROOM ICONS
 * Emoji icons for visual representation (can be replaced with actual icons)
 */
const ROOM_ICONS = {
  living_room: '🛋️',
  dining_room: '🍽️',
  family_room: '📺',
  master_bedroom: '🛏️',
  bedroom: '🛏️',
  guest_room: '🛏️',
  hallway: '🚪',
  stairs: '🪜',
  landing: '🏠',
  basement: '⬇️',
  office: '💼',
  bonus_room: '🎮',
  closet: '👔',
  custom: '📝',
  // --- Commercial ---
  reception: '💁',
  open_office: '🪑',
  private_office: '🚪',
  conference_room: '📋',
  break_room: '☕',
  lobby: '🏛️',
  restroom: '🚻',
  retail_floor: '🛒',
  // --- Multi-Unit ---
  unit_living: '🛋️',
  unit_bedroom: '🛏️',
  unit_hallway: '🚪',
  unit_stairs: '🪜',
  unit_closet: '👔',
  unit_kitchenette: '🍳',
  unit_bathroom: '🚿'
};

/**
 * INSTALLATION COMPLEXITY MULTIPLIERS
 * Some rooms require different installation techniques
 * 
 * Standard rooms: 1.0x ($2.50/sqft base rate)
 * Stairs: 2.5x ($6.25/sqft) - requires special techniques, more waste
 * Hallways: 1.2x ($3.00/sqft) - narrow spaces, more seams
 */
const COMPLEXITY_MULTIPLIERS = {
  // Standard rooms
  living_room: 1.0,
  dining_room: 1.0,
  family_room: 1.0,
  master_bedroom: 1.0,
  bedroom: 1.0,
  guest_room: 1.0,
  basement: 1.0,
  office: 1.0,
  bonus_room: 1.0,
  closet: 1.0,
  landing: 1.0,
  
  // Complex installations
  hallway: 1.2,
  stairs: 2.5,
  
  // --- Commercial ---
  reception: 1.0,
  open_office: 1.0,
  private_office: 1.0,
  conference_room: 1.0,
  break_room: 1.0,
  lobby: 1.0,
  restroom: 1.0,
  retail_floor: 1.0,
  
  // --- Multi-Unit ---
  unit_living: 1.0,
  unit_bedroom: 1.0,
  unit_hallway: 1.2,
  unit_stairs: 2.5,
  unit_closet: 1.0,
  unit_kitchenette: 1.0,
  unit_bathroom: 1.0,
  
  custom: 1.0
};

/**
 * PROPERTY TYPES
 */
const PROPERTY_TYPES = {
  RESIDENTIAL: 'residential',
  COMMERCIAL: 'commercial',
  MULTI_UNIT: 'multi_unit'
};

const PROPERTY_TYPE_LABELS = {
  residential: 'Residential',
  commercial: 'Commercial',
  multi_unit: 'Multi-Unit Property'
};

const PROPERTY_TYPE_DESCRIPTIONS = {
  residential: 'Homes, condos, apartments',
  commercial: 'Offices, retail spaces, hospitality',
  multi_unit: 'Apartment buildings, multi-family properties'
};

/**
 * ROOM OPTIONS BY PROPERTY TYPE
 * Defines which rooms are available for each property type in the quote flow.
 * multi: true means the room can have multiple instances (bedroom 1, 2, 3...)
 * complex: true means the room has a complexity multiplier > 1.0
 */
const ROOM_OPTIONS_BY_TYPE = {
  residential: [
    { id: 'living_room',     label: 'Living Room',    icon: '🛋️', multi: false, complex: false },
    { id: 'dining_room',     label: 'Dining Room',     icon: '🍽️', multi: false, complex: false },
    { id: 'master_bedroom',  label: 'Master Bedroom',  icon: '🛏️', multi: false, complex: false },
    { id: 'bedroom',         label: 'Bedroom',         icon: '🛏️', multi: true,  complex: false },
    { id: 'hallway',         label: 'Hallway',         icon: '🚪', multi: true,  complex: true  },
    { id: 'stairs',          label: 'Stairs',          icon: '🪜', multi: false, complex: true  },
    { id: 'closet',          label: 'Closet',          icon: '👔', multi: true,  complex: false },
    { id: 'basement',        label: 'Basement',        icon: '⬇️', multi: false, complex: false },
    { id: 'office',          label: 'Office / Den',    icon: '💼', multi: false, complex: false },
    { id: 'bonus_room',      label: 'Bonus Room',      icon: '🎮', multi: false, complex: false },
    { id: 'landing',         label: 'Landing',         icon: '🏠', multi: false, complex: false }
  ],
  commercial: [
    { id: 'reception',       label: 'Reception',       icon: '💁', multi: false, complex: false },
    { id: 'open_office',     label: 'Open Office',     icon: '🪑', multi: true,  complex: false },
    { id: 'private_office',  label: 'Private Office',  icon: '🚪', multi: true,  complex: false },
    { id: 'conference_room', label: 'Conference Room', icon: '📋', multi: false, complex: false },
    { id: 'break_room',      label: 'Break Room',      icon: '☕', multi: false, complex: false },
    { id: 'lobby',           label: 'Lobby / Foyer',   icon: '🏛️', multi: false, complex: false },
    { id: 'hallway',         label: 'Hallway',         icon: '🚪', multi: true,  complex: true  },
    { id: 'restroom',        label: 'Restroom',        icon: '🚻', multi: true,  complex: false },
    { id: 'retail_floor',    label: 'Sales Floor',     icon: '🛒', multi: false, complex: false },
    { id: 'stairs',          label: 'Stairs',          icon: '🪜', multi: false, complex: true  }
  ],
  multi_unit: [
    { id: 'unit_living',     label: 'Living Room',     icon: '🛋️', multi: false, complex: false },
    { id: 'unit_bedroom',    label: 'Bedroom',         icon: '🛏️', multi: true,  complex: false },
    { id: 'unit_hallway',    label: 'Hallway',         icon: '🚪', multi: false, complex: true  },
    { id: 'unit_stairs',     label: 'Stairs',          icon: '🪜', multi: false, complex: true  },
    { id: 'unit_closet',     label: 'Closet',          icon: '👔', multi: true,  complex: false },
    { id: 'unit_kitchenette',label: 'Kitchenette',     icon: '🍳', multi: false, complex: false },
    { id: 'unit_bathroom',   label: 'Bathroom',        icon: '🚿', multi: false, complex: false }
  ]
};

/**
 * STEP COPY BY PROPERTY TYPE
 * Route-aware descriptions for each step of the quote flow.
 */
const STEP_COPY_BY_TYPE = {
  residential: {
    step1: "We'll tailor your quote to your home — select one to get started.",
    step2: 'Select the rooms in your home that need carpet.',
    step3: 'Enter the measurements for each room so we can estimate materials.',
    step4: 'Browse our residential carpet collection — curated for your home.',
    step55: 'Choose how you\'d like to handle installation for your home.',
    step5: 'Where should we deliver your carpet?',
    step6: 'How can we reach you with your quote?',
    step7: 'Finding the right installer for your home...',
    step8: 'Review your home project details before submitting.'
  },
  commercial: {
    step1: "We'll tailor your quote for your business space.",
    step2: 'Select the areas in your commercial space that need carpet.',
    step3: 'Enter measurements for each area. For irregular spaces, use the total sq ft option.',
    step4: 'Browse our commercial-grade collection designed for high-traffic areas.',
    step55: 'Choose your service level. For commercial spaces, delivery or full installation?',
    step5: 'Where is the business located for delivery?',
    step6: 'Enter your business contact information.',
    step7: 'Finding installers with commercial experience...',
    step8: 'Review your commercial project details.'
  },
  multi_unit: {
    step1: "We'll set up carpeting for your rental properties.",
    step2: 'Define one unit\'s layout, then duplicate across all your units.',
    step3: 'Measure one unit and we\'ll apply it to all identical units.',
    step4: 'Browse our durable collection built for tenant turnover.',
    step55: 'Choose the right service model for your rental properties.',
    step5: 'Where should we deliver the materials?',
    step6: 'Enter your property management contact information.',
    step7: 'Finding installers with multi-unit experience...',
    step8: 'Review your multi-unit project summary.'
  }
};

/**
 * PRICING CONSTANTS
 * Base rates for estimates (will be refined with actual installer data)
 */
const PRICING = {
  BASE_INSTALLATION_RATE: 2.50,  // $ per sq ft for standard installation
  REMOVAL_RATE: 0.75,             // $ per sq ft for old carpet removal
  PADDING_RATE: 0.50,             // $ per sq ft for padding upgrade
  TAX_RATE: 0.08                  // 8% sales tax (varies by location)
};

/**
 * GLOBAL MATERIAL MARKUP
 * Fixed 15% material markup for ALL customers (Phase 7).
 * One price regardless of relationship — commission split happens on the back end.
 */
const GLOBAL_MATERIAL_MARKUP = 0.15;

/**
 * INSTALLER LOYALTY TIERS
 * Used purely for gamification, reputation, and consumer trust.
 * Financial logic has been decoupled from these tiers.
 */
const LOYALTY_TIERS = {
  BRONZE: {
    name: 'Bronze',
    requirements: { jobs: 0, rating: 0 }
  },
  SILVER: {
    name: 'Silver',
    requirements: { jobs: 5, rating: 4.5 }
  },
  GOLD: {
    name: 'Gold',
    requirements: { jobs: 20, rating: 4.8, certified: true }
  },
  PLATINUM: {
    name: 'Platinum',
    requirements: { jobs: 50, rating: 4.9, certified: true }
  }
};

/**
 * VALIDATION LIMITS
 */
const LIMITS = {
  MIN_SQUARE_FOOTAGE: 1,
  MAX_SQUARE_FOOTAGE: 10000,      // Per room max
  MIN_TOTAL_SQUARE_FOOTAGE: 500,   // Minimum viable job size
  MAX_ROOMS: 20                   // Maximum rooms per quote
};

// =========================================
// SERVICE TIER CONFIGURATION (Phase 7 Update)
// =========================================

/**
 * SERVICE TIERS
 * Three service tiers representing logistical choices, not pricing differences.
 * ALL use GLOBAL_MATERIAL_MARKUP (15%).
 * 
 * - BYOI Certified: Platform-certified installer, quality guarantees.
 * - BYOI Basic: Use your own installer, we coordinate.
 * - Materials Only: Drop-off delivery only.
 */
const SERVICE_TIERS = {
  BYOI_CERTIFIED: {
    id: 'byoi_certified',
    name: 'BYOI Certified',
    description: 'Use a platform-certified installer with quality guarantees and metrics tracking',
    requiresCertification: true,
    includesQMA: true,
    features: [
      'Platform-certified installer',
      'Quality metrics tracking',
      'Quality Management Agreement (QMA)',
      'NCR process for issues',
      'Coordinated material delivery',
      'Priority support'
    ]
  },
  BYOI: {
    id: 'byoi',
    name: 'BYOI Basic',
    description: 'Use your own installer, we coordinate materials and payment',
    features: [
      'Your choice of installer',
      'Coordinated material delivery',
      'Secure payment processing',
      'Installation specs provided',
      'Support available'
    ]
  },
  MATERIALS_ONLY: {
    id: 'materials_only',
    name: 'Materials Only',
    description: 'Just the carpet - installation is 100% your responsibility',
    features: [
      'Premium American-made carpet',
      'Professional cutting diagram',
      'Installation specifications',
      'Shipped to your address',
      'Local installer recommendations'
    ]
  }
};

/**
 * LEAD SOURCE
 * Who originated the customer relationship.
 */
const LEAD_SOURCE = {
  PLATFORM: 'platform',   // CID marketing / organic
  INSTALLER: 'installer'  // Installer referral link
};

/**
 * COMMISSION SPLIT
 * Back-end economics for the fixed-price model.
 * 
 * Decision Tree:
 * - IF Lead Source == INSTALLER: Installer keeps full 12% distributable margin.
 * - IF Lead Source == PLATFORM: 
 *   - Installer receives flat rate (5%) for installing a pre-sold job.
 *   - Sales Agent receives rate (7%) for landing the client.
 */
const COMMISSION_SPLIT = {
  TOTAL_MARGIN: 0.15,           // The 15% markup charged to the customer
  STRIPE_FEE: 0.03,             // ~3% Stripe Connect processing
  DISTRIBUTABLE: 0.12,          // 15% − 3% = 12% available to split
  INSTALLER_FLAT_RATE: 0.05,    // Flat rate for installing a pre-sold job
  SALES_AGENT_RATE: 0.07,       // Rate for landing the client
  INSTALLER_OWN_LEAD: 0.12      // Installer acts as both agent and installer
};

/**
 * VALID SERVICE TIERS
 * Convenience array of tier IDs for validation
 */
const VALID_SERVICE_TIERS = Object.values(SERVICE_TIERS).map(t => t.id);

/**
 * MINIMUM ORDER VALUES
 * Different minimums by tier to ensure profitability
 * Note: Unified $500 minimum across all tiers ensures freight viability and order profitability.
 */
const MINIMUM_ORDER = {
  BYOI_CERTIFIED: 500,     // $500 minimum for BYOI Certified
  BYOI: 500,               // $500 minimum for BYOI Basic
  MATERIALS_ONLY: 500      // $500 minimum for materials only
};

/**
 * ESCROW CONFIGURATION
 * Settings for BYOI payment escrow
 */
const ESCROW_CONFIG = {
  RELEASE_DAYS_AFTER_INSTALL: 3,  // Auto-release payment X days after scheduled date
  REMINDER_DAYS_BEFORE_RELEASE: 1 // Send customer reminder X days before release
};

/**
 * Helper function: Get installation cost for a room
 * @param {string} roomType - Room type constant
 * @param {number} squareFootage - Square footage
 * @returns {number} Installation cost
 */
function calculateInstallationCost(roomType, squareFootage) {
  const multiplier = COMPLEXITY_MULTIPLIERS[roomType] || 1.0;
  return squareFootage * PRICING.BASE_INSTALLATION_RATE * multiplier;
}

/**
 * Helper function: Check if room type requires special handling
 * @param {string} roomType - Room type constant
 * @returns {boolean}
 */
function isComplexRoom(roomType) {
  return COMPLEXITY_MULTIPLIERS[roomType] > 1.0;
}

// Export for use in both frontend and backend
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    ROOM_TYPES,
    ROOM_LABELS,
    ROOM_ICONS,
    COMPLEXITY_MULTIPLIERS,
    PROPERTY_TYPES,
    PROPERTY_TYPE_LABELS,
    PROPERTY_TYPE_DESCRIPTIONS,
    ROOM_OPTIONS_BY_TYPE,
    STEP_COPY_BY_TYPE,
    PRICING,
    LIMITS,
    SERVICE_TIERS,
    MINIMUM_ORDER,
    ESCROW_CONFIG,
    VALID_QUOTE_TYPES,
    LEGACY_QUOTE_TYPE_MAPPING,
    GLOBAL_MATERIAL_MARKUP,
    LOYALTY_TIERS,
    LEAD_SOURCE,
    COMMISSION_SPLIT,
    VALID_SERVICE_TIERS,
    calculateInstallationCost,
    isComplexRoom
  };
}
